import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Bell,
  Check,
  CheckCheck,
  AlertTriangle,
  Info,
  X,
  Clock,
  Share2,
  MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NotificationBell = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [user]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newCount = response.data.unread_count || 0;
      
      // Trigger animation if count increased
      if (newCount > previousUnreadCount && previousUnreadCount > 0) {
        setHasNewNotification(true);
        setTimeout(() => setHasNewNotification(false), 3000);
      }
      
      setPreviousUnreadCount(unreadCount);
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [user, previousUnreadCount, unreadCount]);

  // Initial fetch
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Fetch notifications when popover opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Poll for new notifications every 60 seconds
  useEffect(() => {
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return isDarkMode ? 'text-red-400 bg-red-500/20' : 'text-red-500 bg-red-50';
      case 'medium': return isDarkMode ? 'text-yellow-400 bg-yellow-500/20' : 'text-yellow-500 bg-yellow-50';
      default: return isDarkMode ? 'text-blue-400 bg-blue-500/20' : 'text-blue-500 bg-blue-50';
    }
  };

  const getPriorityIcon = (priority, type) => {
    // Special icons for specific notification types
    if (type === 'campaign_shared') return <Share2 className="h-4 w-4" />;
    if (type === 'campaign_update') return <MessageSquare className="h-4 w-4" />;
    
    switch (priority) {
      case 'high': return <AlertTriangle className="h-4 w-4" />;
      default: return <Info className="h-4 w-4" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`relative ${hasNewNotification ? 'animate-bounce' : ''}`}
          data-testid="notification-bell"
        >
          <Bell className={`h-5 w-5 transition-colors ${
            hasNewNotification 
              ? 'text-indigo-500' 
              : unreadCount > 0 
                ? (isDarkMode ? 'text-indigo-400' : 'text-indigo-600')
                : ''
          }`} />
          {unreadCount > 0 && (
            <span className={`absolute -top-1 -right-1 h-5 w-5 rounded-full text-white text-xs flex items-center justify-center font-medium transition-all ${
              hasNewNotification 
                ? 'bg-indigo-500 animate-pulse scale-110' 
                : 'bg-red-500'
            }`}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className={`w-80 p-0 ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`} align="end">
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDarkMode ? 'border-[hsl(220,14%,22%)]' : ''}`}>
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : ''}`}>Notifications</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllAsRead}
              className={`text-xs ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Tout marquer comme lu
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-80">
          {notifications.length === 0 ? (
            <div className={`flex flex-col items-center justify-center h-full py-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              <Bell className="h-10 w-10 mb-2 opacity-50" />
              <p className="text-sm">Aucune notification</p>
            </div>
          ) : (
            <div className={`divide-y ${isDarkMode ? 'divide-[hsl(220,14%,22%)]' : ''}`}>
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 transition-colors cursor-pointer ${
                    !notification.read 
                      ? (isDarkMode ? 'bg-indigo-600/10' : 'bg-indigo-50/50')
                      : ''
                  } ${isDarkMode ? 'hover:bg-[hsl(220,14%,18%)]' : 'hover:bg-slate-50'}`}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${getPriorityColor(notification.priority)}`}>
                      {getPriorityIcon(notification.priority, notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium truncate ${
                          !notification.read 
                            ? (isDarkMode ? 'text-white' : 'text-slate-900')
                            : (isDarkMode ? 'text-slate-300' : 'text-slate-600')
                        }`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <span className="h-2 w-2 rounded-full bg-indigo-600 flex-shrink-0" />
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 line-clamp-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {notification.message}
                      </p>
                      <div className={`flex items-center gap-1 mt-1 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        <Clock className="h-3 w-3" />
                        {formatTime(notification.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {notifications.length > 0 && (
          <div className={`border-t px-4 py-2 ${isDarkMode ? 'border-[hsl(220,14%,22%)]' : ''}`}>
            <Button variant="ghost" className={`w-full text-sm ${isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-600'}`}>
              Voir toutes les notifications
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
